"use client";

import { CallStatus } from "@prisma/client";
import ActionMenu from "@/app/(components)/generics/ActionMenu/ActionMenu";
import IconCircleMinus from "@/app/(components)/generics/Icons/IconCircleMinus";
import IconDownload from "@/app/(components)/generics/Icons/IconDownload";
import IconExternalLink from "@/app/(components)/generics/Icons/IconExternalLink";
import IconRestart from "@/app/(components)/generics/Icons/IconRestart";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import {
  useBrand,
  useCanManageCompany,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { useCallInsightScope } from "@/app/hooks/useCallInsightScope";
import { ModalStore } from "@/app/stores/ModalStore";
import { UserStore } from "@/app/stores/UserStore";
import { TKeyCallAnalysis as TKey } from "@/app/translations/keys/TKeyCallAnalysis";
import { useT } from "@/app/translations/useT";
import { CallFile } from "@/app/types/call/CallFile";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import RunAnalysis, { RunAnalysisMode } from "../RunAnalysis/RunAnalysis";

type Props = DefaultProps & {
  callFile: CallFile;
  onOpen: () => void;
  onRemove: () => void;
  onStatusUpdate: (status: CallStatus) => void;
};

export default function CallRowMenu(props: Props) {
  const T = useT();
  const { params, callFile, onOpen, onRemove, onStatusUpdate } = props;

  const scope = useCallInsightScope(params, callFile);

  const isAnalyzing = !callFile.status || callFile.status === CallStatus.ANALYZING;
  const isTenant = useBrand() !== null;
  const canManageCompany = useCanManageCompany();
  const currentUserId = UserStore((state) => state.user?.id);
  // Managers manage every call; a plain member may still delete the calls they uploaded.
  const canManage = !isTenant || canManageCompany;
  const canDelete = canManage || callFile.savedCall?.uploaderId === currentUserId;

  const handleOpen = () => {
    onOpen();
  };

  const handleRemove = () => {
    onRemove();
  };

  return (
    <ActionMenu
      items={[
        {
          icon: <IconExternalLink size={15} />,
          label: T(TKey.CALL_ROW_MENU_OPEN) as string,
          onClick: handleOpen,
        },
        !isAnalyzing && {
          customNode: (
            <RunAnalysis
              params={params}
              callId={callFile.id}
              onStart={(newStatus) => {
                onStatusUpdate(newStatus);
              }}
              mode={RunAnalysisMode.MENU}
              callFile={callFile}
            />
          ),
        },
        callFile.status === CallStatus.ANALYZED && {
          icon: <IconDownload size={15} />,
          label: T(TKey.CALL_EXPORT_CALL_SUBMIT) as string,
          onClick: () => {
            ModalStore.getState().openExportCall(params, callFile.id);
          },
        },
        scope.available &&
          !scope.recounting &&
          !scope.excluded && {
            icon: <IconCircleMinus size={16} />,
            label: T(TKey.CALL_ANALYSIS_EXCLUDE_CTA) as string,
            onClick: scope.handleExclude,
          },
        scope.available &&
          !scope.recounting &&
          scope.excluded &&
          scope.canRunPaidActions && {
            icon: <IconRestart size={15} />,
            label: T(TKey.CALL_ANALYSIS_RECOUNT_CTA) as string,
            onClick: scope.handleRecount,
          },
        canDelete && { isDivider: true },
        canDelete && {
          icon: <IconTrash />,
          label: T(TKey.CALL_DETAIL_DELETE) as string,
          onClick: handleRemove,
          danger: true,
        },
      ]}
    />
  );
}
